import React from 'react';

const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');
const money = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

const bucketBadge = (row) => {
  if (row.payment_status === 'paid') return ['Paid', 'bg-green-100 text-green-700'];
  if (row.bucket === 'overdue') return [`Overdue${row.days_overdue ? ` ${row.days_overdue}d` : ''}`, 'bg-red-100 text-red-700'];
  if (row.bucket === 'upcoming') return ['Upcoming', 'bg-gray-100 text-gray-600'];
  return ['Due', 'bg-blue-100 text-blue-700'];
};

export default function PaymentsTable({
  rows, loading, onRecord, onReminder, onEditDue, onHistory,
  selectedIds, onToggleRow, onToggleAll, failed = false,
}) {
  // Selection is opt-in: the table stays exactly as it was when the page does not
  // pass the handlers, so nothing else rendering this table has to change.
  const selectable = Boolean(onToggleRow && onToggleAll && selectedIds);
  const isSelected = (r) => Boolean(selectedIds && selectedIds.has(r.sub_order_id));

  if (loading) return <div className="p-8 text-center text-gray-400">Loading…</div>;
  // "No challans found" is a statement about the data. Don't make it when the
  // request failed — the banner above says what actually happened.
  if (!rows || rows.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        {failed ? 'Could not load challans.' : 'No challans found.'}
      </div>
    );
  }

  const selectedOnPage = rows.filter(isSelected).length;
  const allSelected = selectedOnPage > 0 && selectedOnPage === rows.length;
  const someSelected = selectedOnPage > 0;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase">
            {selectable && (
              <th className="px-3 py-2 w-8">
                <input
                  type="checkbox"
                  aria-label="Select all challans on this page"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => onToggleAll(!allSelected)}
                />
              </th>
            )}
            <th className="text-left px-3 py-2">Challan</th>
            <th className="text-left px-3 py-2">Customer</th>
            <th className="text-left px-3 py-2">Terms</th>
            <th className="text-left px-3 py-2">Due Date</th>
            <th className="text-right px-3 py-2">Due (₹)</th>
            <th className="text-right px-3 py-2">Paid (₹)</th>
            <th className="text-right px-3 py-2">Outstanding (₹)</th>
            <th className="text-center px-3 py-2">Status</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const [label, cls] = bucketBadge(r);
            return (
              <tr
                key={r.sub_order_id}
                className={`border-b hover:bg-gray-50 ${isSelected(r) ? 'bg-blue-50/60' : ''}`}
              >
                {selectable && (
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      aria-label={`Select challan ${r.challan_number || r.sub_order_id}`}
                      checked={isSelected(r)}
                      onChange={() => onToggleRow(r.sub_order_id)}
                    />
                  </td>
                )}
                <td className="px-3 py-2">
                  <div className="font-semibold text-gray-800">{r.challan_number || '—'}</div>
                  <div className="text-xs text-gray-400">{fmt(r.challan_date)}</div>
                </td>
                <td className="px-3 py-2">{r.customer_name || '—'}</td>
                <td className="px-3 py-2 text-gray-600">{r.payment_terms || '—'}</td>
                <td className="px-3 py-2">
                  {fmt(r.due_date)}
                  {r.due_date_overridden && (
                    <span title={r.due_date_remark || ''} className="ml-1 text-[10px] text-blue-500">✎</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">{money(r.total_amount)}</td>
                <td className="px-3 py-2 text-right">{money(r.amount_paid)}</td>
                <td className="px-3 py-2 text-right font-semibold text-red-600">{money(r.outstanding)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>
                  {r.payment_status === 'partial' && (
                    <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Partial</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {r.payment_status !== 'paid' && (
                    <button onClick={() => onRecord(r)} className="text-green-700 hover:underline mr-2">Pay</button>
                  )}
                  <button onClick={() => onHistory(r)} className="text-gray-600 hover:underline mr-2">History</button>
                  <button onClick={() => onEditDue(r)} className="text-blue-600 hover:underline mr-2">Due date</button>
                  {r.payment_status !== 'paid' && (
                    <button onClick={() => onReminder(r)} className="text-indigo-600 hover:underline">Remind</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
